import * as THREE from 'three';

export const HEAL_TILE_FEEDBACK_MS = 1800;

export const getHealingTileIndex = (props, physicalLanding = null) => {
  const physicallySettled =
    physicalLanding &&
    (!props.relaunch || physicalLanding.relaunchId === props.relaunch.id);
  if (
    (props.phase !== 'revealed' &&
      !(props.phase === 'rolling' && physicallySettled)) ||
    props.concealFacesUntilSettled ||
    (props.relaunch && !physicallySettled) ||
    props.encounter?.combat ||
    props.coreBoss?.combat
  )
    return null;
  const index = Number.isInteger(props.boardActiveIndex)
    ? props.boardActiveIndex
    : physicallySettled
    ? physicalLanding.index
    : props.settledLandingIndex;
  const effect = props.doorBoard?.[index]?.effect;
  return Number.isInteger(index) && effect?.type === 'heal' && effect.amount > 0
    ? index
    : null;
};

export const createHealingTileScene = (board, surfaceY) => {
  const group = new THREE.Group();
  group.name = 'healing-tile-aura';
  group.visible = false;
  board.add(group);
  const material = (opacity) =>
    new THREE.MeshBasicMaterial({
      color: '#83ffc3',
      transparent: true,
      opacity,
      depthWrite: false,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
      toneMapped: false,
    });
  const floor = new THREE.Mesh(
    new THREE.PlaneGeometry(2.55, 2.55),
    material(0.16)
  );
  floor.rotation.x = -Math.PI / 2;
  group.add(floor);
  const rings = [0, 1].map(() => {
    const ring = new THREE.Mesh(
      new THREE.RingGeometry(0.9, 1, 64),
      material(0.65)
    );
    ring.rotation.x = -Math.PI / 2;
    group.add(ring);
    return ring;
  });
  const crossGeometry = new THREE.BoxGeometry(0.36, 0.09, 0.05);
  const crosses = Array.from({ length: 12 }, (_, index) => {
    const cross = new THREE.Group();
    cross.name = `healing-plus-${index}`;
    const surface = material(0.85);
    const horizontal = new THREE.Mesh(crossGeometry, surface);
    const vertical = new THREE.Mesh(crossGeometry, surface);
    vertical.rotation.z = Math.PI / 2;
    cross.add(horizontal, vertical);
    group.add(cross);
    return cross;
  });
  const light = new THREE.PointLight('#67ffaf', 0, 5, 1.5);
  light.position.y = 1;
  group.add(light);
  let activeIndex = null,
    startedAt = 0,
    tileMaterial = null,
    previousIntensity = 0;
  const previousEmissive = new THREE.Color();
  const restore = () => {
    if (!tileMaterial) return;
    tileMaterial.emissive.copy(previousEmissive);
    tileMaterial.emissiveIntensity = previousIntensity;
    tileMaterial = null;
  };
  return {
    get active() {
      return group.visible;
    },
    update(index, elapsed, reduceMotion, tiles, camera) {
      if (!Number.isInteger(index) || !tiles[index]) {
        restore();
        activeIndex = null;
        group.visible = false;
        return;
      }
      if (activeIndex !== index) {
        restore();
        activeIndex = index;
        startedAt = elapsed;
        tileMaterial = tiles[index].material;
        previousEmissive.copy(tileMaterial.emissive);
        previousIntensity = tileMaterial.emissiveIntensity;
        group.position.set(
          ((index % 5) - 2) * 2.85,
          surfaceY + 0.1,
          (Math.floor(index / 5) - 2) * 2.85
        );
      }
      const age = elapsed - startedAt;
      if (age * 1000 >= HEAL_TILE_FEEDBACK_MS) {
        restore();
        group.visible = false;
        return;
      }
      group.visible = true;
      const fade = reduceMotion ? 1 : Math.min(1, (1.8 - age) / 0.4);
      tileMaterial.emissive.set('#25ba79');
      tileMaterial.emissiveIntensity = reduceMotion
        ? 0.9
        : 0.9 + Math.sin(age * 5) * 0.25;
      light.intensity = fade * 4;
      floor.material.opacity = fade * 0.16;
      rings.forEach((ring, index) => {
        const travel = reduceMotion
          ? index * 0.25
          : (age * 0.7 + index * 0.5) % 1;
        ring.scale.setScalar(0.8 + travel * 0.7);
        ring.position.y = 0.02;
        ring.material.opacity = fade * (1 - travel) * 0.65;
      });
      crosses.forEach((cross, index) => {
        const angle = (index * Math.PI) / 6;
        const travel = reduceMotion ? 0.3 : (age * 0.6 + index / 12) % 1;
        const radius = 0.9 + (index % 3) * 0.16;
        cross.position.set(
          Math.cos(angle) * radius,
          0.15 + travel * 1.4,
          Math.sin(angle) * radius
        );
        if (camera) cross.quaternion.copy(camera.quaternion);
        cross.scale.setScalar(0.7 + (index % 3) * 0.15);
        cross.children[0].material.opacity =
          fade * (reduceMotion ? 0.9 : 1 - travel * 0.65);
      });
    },
  };
};
